import React, { useEffect, useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router-dom'
import { AppBar, Toolbar, Drawer, Box, useTheme, useMediaQuery } from '@mui/material'
import ThemeContext from '../contexts/ThemeContext'
import Navbar from './Navbar'
import Sidebar from './Sidebar'

const Layout = ({ children = null }) => {
  const { darkTheme, lightTheme } = useContext(ThemeContext)
  const theme = useTheme()
  const location = useLocation()

  // Get current breakpoint
  const isMd = useMediaQuery(theme.breakpoints.up('md'))
  const isSm = useMediaQuery(theme.breakpoints.up('sm'))

  // Check if current route should hide sidebar (HomePage routes)
  const hideSidebar = location.pathname === '/home' || location.pathname === '/'

  // Drawer state for mobile
  const [mobileOpen, setMobileOpen] = useState(false)

  // Calculate drawer width based on breakpoint
  const drawerWidth = isMd ? 240 : isSm ? 220 : 200

  // Drawer variant: temporary for mobile, permanent for desktop
  const drawerVariant = isMd ? 'permanent' : 'temporary'

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }

  useEffect(() => {
    const darkQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const lightQuery = window.matchMedia('(prefers-color-scheme: light)')

    const handleDarkChange = (e) => {
      if (e.matches) darkTheme()
    }
    const handleLightChange = (e) => {
      if (e.matches) lightTheme()
    }

    darkQuery.addEventListener('change', handleDarkChange)
    lightQuery.addEventListener('change', handleLightChange)

    return () => {
      darkQuery.removeEventListener('change', handleDarkChange)
      lightQuery.removeEventListener('change', handleLightChange)
    }
  }, [darkTheme, lightTheme])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}>
      {/* Fixed Header */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark'
              ? theme.palette.background.sidebar || theme.palette.background.default // Pure black in dark mode
              : theme.palette.background.paper,
          color: 'text.primary',
          boxShadow: 'none',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64, md: 64 },
            padding: { xs: '0 8px', sm: '0 16px', md: '0 24px' },
          }}>
          <Navbar onMenuClick={!isMd ? handleDrawerToggle : undefined} />
        </Toolbar>
      </AppBar>

      {/* Sidebar - Temporary for mobile, Permanent for desktop */}
      {!hideSidebar && (
        <Drawer
          variant={drawerVariant}
          open={isMd ? true : mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            // Mobile: zIndex should be higher than AppBar to appear on top
            ...(!isMd && {
              zIndex: (theme) => theme.zIndex.drawer + 2,
            }),
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? theme.palette.background.sidebar || theme.palette.background.default // Pure black in dark mode
                  : theme.palette.background.paper,
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: isMd
                ? 'none'
                : (theme) =>
                    theme.palette.mode === 'light'
                      ? '0 8px 10px 1px rgba(0, 0, 0, 0.14), 0 3px 14px 2px rgba(0, 0, 0, 0.12)'
                      : '0 8px 10px 1px rgba(0, 0, 0, 0.3), 0 3px 14px 2px rgba(0, 0, 0, 0.4)',
              ...(isMd
                ? {
                    marginTop: `${theme.mixins.toolbar.minHeight}px`,
                    height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
                    zIndex: theme.zIndex.drawer,
                  }
                : {
                    height: '100vh',
                    top: 0,
                    zIndex: (theme) => theme.zIndex.drawer + 2,
                    // No paddingTop here, Sidebar will handle its own header
                  }),
            },
            // Hide drawer on mobile when closed
            ...(drawerVariant === 'temporary' &&
              !mobileOpen && {
                display: 'none',
              }),
          }}>
          <Sidebar onItemClick={!isMd ? handleDrawerToggle : undefined} isMobile={!isMd} />
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginLeft: !hideSidebar && isMd ? `${drawerWidth}px` : 0,
          padding: { xs: 2, sm: 2.5, md: 3 },
          paddingTop: {
            xs: `calc(${theme.mixins.toolbar.minHeight}px + ${theme.spacing(2)})`,
            sm: `calc(${theme.mixins.toolbar.minHeight}px + ${theme.spacing(2.5)})`,
            md: `calc(${theme.mixins.toolbar.minHeight}px + ${theme.spacing(3)})`,
          },
          minHeight: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
          backgroundColor: 'background.default',
          transition: theme.transitions.create(['margin', 'padding'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}>
        {children}
      </Box>
    </Box>
  )
}

Layout.propTypes = {
  children: PropTypes.node,
}

export default Layout
